import React from 'react';
import { LuPackage, LuShoppingCart, LuReceipt } from 'react-icons/lu';

export const QuickStats = ({ 
  listings, 
  orders, 
  sellerStats, 
  onNavigateToListings, 
  onNavigateToOrders, 
  onNavigateToAnalytics 
}) => {
  
  // Format large numbers with abbreviations
  const formatLargeNumber = (num) => {
    if (!num && num !== 0) return '0';
    const number = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(number)) return '0';
    
    if (number >= 1000000) return `${(number / 1000000).toFixed(1)}M`;
    if (number >= 1000) return `${(number / 1000).toFixed(1)}K`;
    return number.toString();
  };

  // Format revenue
  const formatRevenue = (revenue) => {
    if (!revenue && revenue !== 0) return '₦0';
    const amount = typeof revenue === 'string' ? parseFloat(revenue) : revenue;
    if (isNaN(amount)) return '₦0';
    
    if (amount >= 1000000) {
      if (amount >= 1000000000) return `₦${(amount / 1000000000).toFixed(1)}B`;
      return `₦${(amount / 1000000).toFixed(1)}M`;
    }
    return `₦${amount.toLocaleString('en-NG')}`;
  };

  // Calculate stats
  const activeListings = listings?.filter(l => l.status === 'active')?.length || 0;
  const pendingOrders = orders?.filter(o => o.status === 'pending' || o.status === 'offer_pending')?.length || 0;

  // Mobile stats - grid layout like QuickActions
  const mobileStats = [
    {
      id: 'listings',
      icon: LuPackage,
      title: 'Listings',
      value: listings?.length || 0,
      subtitle: `${activeListings} active`,
      color: 'from-orange-500 to-red-500',
      onClick: onNavigateToListings
    },
    {
      id: 'orders',
      icon: LuShoppingCart,
      title: 'Orders',
      value: orders?.length || 0,
      subtitle: `${pendingOrders} pending`,
      color: 'from-green-500 to-emerald-500',
      onClick: onNavigateToOrders
    },
    {
      id: 'revenue',
      icon: LuReceipt,
      title: 'Revenue',
      value: formatRevenue(sellerStats?.total_revenue || 0),
      subtitle: 'All time',
      color: 'from-blue-500 to-indigo-500',
      onClick: onNavigateToAnalytics
    }
  ];

  // Desktop stats (cards) - UNCHANGED
  const desktopStats = [
    {
      id: 'listings',
      icon: LuPackage,
      value: listings?.length || 0,
      label: 'Listings',
      subtext: `${activeListings} active`,
      onPress: onNavigateToListings,
      color: '#ff660e',
      bgColor: 'bg-gradient-to-br from-orange-50 to-red-50'
    },
    {
      id: 'orders',
      icon: LuShoppingCart,
      value: orders?.length || 0,
      label: 'Orders',
      subtext: `${pendingOrders} pending`,
      onPress: onNavigateToOrders,
      color: '#ff660e',
      bgColor: 'bg-gradient-to-br from-orange-50 to-red-50'
    },
    {
      id: 'revenue',
      icon: LuReceipt,
      value: formatRevenue(sellerStats?.total_revenue || 0),
      label: 'Revenue',
      subtext: 'All time',
      onPress: onNavigateToAnalytics,
      color: '#ff660e',
      bgColor: 'bg-gradient-to-br from-orange-50 to-red-50'
    }
  ];

  return (
    <>
      {/* Mobile View - Grid like QuickActions (3 items) */}
      <div className="lg:hidden mb-6">
        <h2 className="text-lg font-bold text-black mb-4">Store Stats</h2>
        <div className="grid grid-cols-3 gap-3">
          {mobileStats.map((stat) => {
            const Icon = stat.icon;
            return (
              <button
                key={stat.id}
                onClick={stat.onClick}
                className="flex flex-col items-center p-3 bg-white rounded-xl shadow-sm border border-gray-200 hover:border-orange-200 hover:shadow transition-all focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <div className={`p-2 rounded-full bg-gradient-to-r ${stat.color} mb-2`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                
                <p className="text-lg font-bold text-black mb-1">
                  {stat.value}
                </p>
                
                <span className="text-xs font-medium text-gray-900 text-center mb-1">
                  {stat.title}
                </span>
                
                <span className="text-xs text-gray-500 text-center">
                  {stat.subtitle}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Desktop View - Cards (UNCHANGED) */}
      <div className="hidden lg:grid lg:grid-cols-3 gap-6 mb-6">
        {desktopStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <button
              key={stat.id}
              onClick={stat.onPress}
              className="bg-white rounded-2xl shadow-lg p-5 hover:shadow-xl transition-all group focus:outline-none focus:ring-2 focus:ring-orange-500 text-left"
            >
              <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${stat.bgColor} group-hover:scale-105 transition-transform mb-3`}>
                <Icon className="w-6 h-6" style={{ color: stat.color }} />
              </div>
              
              <p className="text-2xl font-bold text-black mb-1">
                {stat.value}
              </p>
              
              <p className="text-sm font-semibold text-gray-900 mb-1">
                {stat.label}
              </p>
              
              <p className="text-xs text-gray-500">
                {stat.subtext}
              </p>
            </button>
          );
        })}
      </div>
    </>
  );
};

export default QuickStats;